import { createFileRoute } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { pageHeadScripts } from "@/lib/pageJsonLd";
import { RegionPage } from "@/components/RegionPage";
import hero from "@/assets/original/hero-shade.jpg";

export const Route = createFileRoute("/hill-country")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Texas Hill Country Awnings, Covers & Shade | Boerne, Kerrville, Fredericksburg | Texas Made Shade",
      description:
        "Custom awnings, pergola covers, sail shades and outdoor canvas for the Texas Hill Country — Boerne, Kerrville, Fredericksburg, Bandera and Comfort.",
      keywords:
        "Texas Hill Country awnings, Hill Country pergola covers, Boerne awnings, Kerrville awnings, Fredericksburg awnings, Bandera awnings, sail shades Hill Country, patio covers Hill Country",
      image: hero,
      socialTitle: "Hill Country Awnings & Shade — Texas Made Shade",
      socialDescription:
        "Welded aluminum + Sunbrella canvas. Serving Boerne, Kerrville, Fredericksburg, Bandera and the Texas Hill Country.",
    }),
    scripts: pageHeadScripts({
      path: "/hill-country",
      name: "Texas Hill Country Awnings, Covers & Shade",
      description: "Custom awnings, pergola covers, sail shades and outdoor canvas for the Texas Hill Country — Boerne, Kerrville, Fredericksburg, Bandera and Comfort.",
      faqs: [
        { q: "Do you have experience with Hill Country wind exposure?", a: "Yes — most Hill Country installs need heavier post anchoring and additional cross-bracing. We engineer every frame for the ridge-line and river-bottom wind patterns specific to your site." },
        { q: "Can you match the look of cedar, metal-roof Hill Country homes?", a: "Powder-coated bronze or matte black frames with natural-tone Sunbrella® canvas pair well with cedar and limestone. We bring swatches on-site so you can hold them against the house." },
        { q: "Do you install at wineries, tasting rooms and event venues?", a: "Frequently. Sail shades and pergola covers for Fredericksburg and Hill Country wineries are some of our most-requested commercial work." },
      ],
    }),
  }),
  component: () => (
    <RegionPage
      region="Texas Hill Country"
      tagline="Ranch homes, river houses and Hill Country wineries call for shade that looks at home in cedar, limestone and live oak — and stands up to the wind."
      intro="The Texas Hill Country has its own design language: cedar posts, limestone patios, metal roofs and big sky. Our awnings and pergola covers are built to complement that look — black or bronze powder-coated aluminum, natural-tone Sunbrella® canvas, and frames engineered for the open-ridge winds that funnel through Bandera, Boerne and Fredericksburg. We've covered everything from working ranches to wedding venues."
      cities={[
        { to: "/boerne", name: "Boerne", county: "Kendall Co." },
        { to: "/kerrville", name: "Kerrville", county: "Kerr Co." },
        { to: "/fredericksburg", name: "Fredericksburg", county: "Gillespie Co." },
        { to: "/bandera", name: "Bandera", county: "Bandera Co." },
        { to: "/comfort", name: "Comfort", county: "Kendall Co." },
        { to: "/bulverde", name: "Bulverde", county: "Comal Co." },
        { to: "/spring-branch", name: "Spring Branch", county: "Comal Co." },
        { to: "/canyon-lake", name: "Canyon Lake", county: "Comal Co." },
        { to: "/wimberley", name: "Wimberley", county: "Hays Co." },
        { to: "/dripping-springs", name: "Dripping Springs", county: "Hays Co." },
        { to: "/helotes", name: "Helotes", county: "Bexar Co." },
      ]}
      highlights={[
        { to: "/pergola-covers", title: "Patio, Gazebo & Pergola Covers", desc: "Free-standing and attached patio covers, plus pergola tops on black or bronze aluminum frames sized for Hill Country patios and pool decks." },
        { to: "/awnings", title: "Custom Awnings", desc: "Window, door and porch awnings finished to match cedar and limestone exteriors." },
        { to: "/sail-shades", title: "Sail Shades", desc: "Wineries, tasting rooms and event venues — tensioned shade with clean sightlines." },
        { to: "/curtains", title: "Outdoor Curtains", desc: "Roll-down panels for Hill Country evenings, hunting lodges and guest cabins." },
        { to: "/deer-blind-covers", title: "Deer Blind & Hunting Canvas", desc: "Custom blind tops and ranch covers built for working Hill Country properties." },
      ]}
      faqs={[
        {
          q: "Do you have experience with Hill Country wind exposure?",
          a: "Yes — most Hill Country installs need heavier post anchoring and additional cross-bracing. We engineer every frame for the ridge-line and river-bottom wind patterns specific to your site.",
        },
        {
          q: "Can you match the look of cedar, metal-roof Hill Country homes?",
          a: "Powder-coated bronze or matte black frames with natural-tone Sunbrella® canvas pair well with cedar and limestone. We bring swatches on-site so you can hold them against the house.",
        },
        {
          q: "Do you install at wineries, tasting rooms and event venues?",
          a: "Frequently. Sail shades and pergola covers for Fredericksburg and Hill Country wineries are some of our most-requested commercial work.",
        },
        {
          q: "How far into the Hill Country will you travel?",
          a: "From Boerne and Bandera up through Kerrville, Fredericksburg, Comfort and out to Llano — if it's Hill Country, we'll come spec it.",
        },
      ]}
    />
  ),
});
